import { createRouter, createWebHistory, type RouteRecordNormalized } from 'vue-router';
import { useAuthUser, useGlobals } from '@/helpers/globals';
import { apiUrl, appUrl, instance as apiInstance } from '@/helpers/api';
import Home from '@/views/HomeView.vue';
import guestRoutes from './guest_routes';
import planungsgeschaefteRoutes from './planungsgeschaefte_routes';
import useraccountRoutes from './user_routes';
import adminRoutes from './admin_routes';
import { nextTick } from 'vue';


/**
 * Possible meta data values:
 * - requiresAuth: true, OR
 * - guest: true: one of those must be set
 *
 * - breadcrumbTitle: string/function for the breadcrumb display title of that route
 * - breadcrumbTo: route url / route config object for alternative route this breadcrumb points to
 * - skipBreadcrumb: does not show a breadcrumb entry for this specific (sub)route
 * - hideBreadcrumb: does not show the breadcrumbs at all for this specific route
 */
const rootRoutes = [
    {
        path: '/',
        name: 'home',
        component: Home,
        meta: {
            guest: true,
            hideBreadcrumb: true
        }
    }
];

const routes = [...guestRoutes, ...rootRoutes, ...planungsgeschaefteRoutes, ...adminRoutes, ...useraccountRoutes];
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});

router.beforeEach((to, from) => {
    document.body.style.cursor = 'wait';

    const api = apiInstance();
    if (api && useAuthUser()) {
        if (api.lastAction) {
            if (new Date().getTime() - api.lastAction > ((useGlobals().session_lifetime || 1440) - 1) * 1000) {
                window.location.href = appUrl('?logout');
            }
        }
        api.lastAction = new Date().getTime();
    }

    // Auth requiring routes need an auth user:
    if (to.matched.some((rec) => rec.meta.requiresAuth)) {
        if (!useAuthUser()) {
            const fromUrl = window.location.href;
            window.location.href = apiUrl('/oidc/login', { url: encodeURIComponent(fromUrl) });
            return;
            /*return next({
              name: 'login',
              query: {
                  redirect: to.path
              }
          });*/
        } else {
            // Check if the route needs specific privileges
            const user = useAuthUser();

            const allRoutesAllowed =
                user?.isAdmin ||
                to.matched.every((routeRec) => {
                    if (routeRec.meta.roles && (routeRec.meta.roles as string[]).length > 0) {
                        for (const neededRole of routeRec.meta.roles as string[]) {
                            switch (neededRole) {
                                case 'admin':
                                    return user?.isAdmin ?? false;
                                default:
                                    return false;
                            }
                        }
                        // no role matches
                        return false;
                    } else {
                        // no roles needed
                        return true;
                    }
                });
            if (allRoutesAllowed) {
                return true;
            } else {
                // no access, abort
                return false;
            }
        }
    } else if (to.matched.some((rec) => rec.meta.guest)) {
        // no auth needed - guest routes are allowed for everyone
        return true;
    } else {
        // Oops - we have a non-specified access on the route. Abort.
        console.error('Unspecified access on route. Specify meta.guest / meta.requiresAuth: ', to);
        return { name: 'home' };
    }
});

router.afterEach((to, from) => {
    document.body.style.cursor = 'initial';

    // see https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609 why we use
    // nextTick here:
    nextTick(() => {
        // Update the page's title, based on the Route's meta data, if applicable:
        let title = (to.meta && to.meta.title) || null;
        if (title instanceof Function) {
            title = title(to, from);
        }
        document.title = (title || '') + (title ? ' - ePlan' : 'ePlan');
    });
});

export default router;

export function routerBreadcrumbTitle(route: RouteRecordNormalized): string {
    const title = route.meta?.breadcrumbTitle || routerTitle(route);
    if (title instanceof Function) {
        return title();
    } else if (typeof title === 'string') {
        return title || '(unknown)';
    } else {
        return '(unknown)';
    }
}

export function routerTitle(route: RouteRecordNormalized): string {
    const title = route.meta?.title || null;
    if (title instanceof Function) {
        return title();
    } else if (typeof title === 'string') {
        return title || '(unknown)';
    } else {
        return '(unknown)';
    }
}
